import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Divider,
  Paper,
  Container,
  Stack,
  useTheme,
  useMediaQuery,
  Badge,
  Button
} from '@mui/material';
import {
  Security,
  Person,
  Business,
  CalendarToday,
  Schedule,
  CardTravel
} from '@mui/icons-material';
import { Activity, ActivityCard } from './ActivityCard';
import { getLatestActivities } from './WorkerAccessCardHelper';
import { formatDate } from 'utils/functions';



type WorkerData = {
  photoUrl: string;
  securityId: string;
  firstName: string;
  lastName: string;
  supplier: string;
  role: string;
  birthDate: string;
  activities: Activity[];
};

const workerData: WorkerData = {
  photoUrl: "https://randomuser.me/api/portraits/men/32.jpg",
  securityId: "SEC123456",
  firstName: "João",
  lastName: "Silva",
  supplier: "Fornecedor XYZ",
  role: "Operador de Campo",
  birthDate: "1990-05-15",
  activities: [
    {
      code: "ATV001",
      name: "Treinamento de Segurança",
      status: "Concluído",
      dueDate: "2025-08-20",
    },
    {
      code: "ATV002",
      name: "Integração",
      status: "Pendente",
      dueDate: "2025-09-01",
    },
    {
      code: "ATV003",
      name: "Treinamento de Segurança Avançado",
      status: "Vencido",
      dueDate: "2025-09-10",
    },
    {
      code: "ATV004",
      name: "Treinamento de Segurança Padrão",
      status: "Vencido",
      dueDate: "2025-09-10",
    },
    {
      code: "ATV005",
      name: "Trainamento",
      status: "Vencido",
      dueDate: "2025-09-10",
    }
  ]
};


const InfoField = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
    <Box sx={{ color: 'primary.main', mt: 0.5 }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);


export default function WorkerAccessCardPage() {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showMore, setShowMore] = useState(false);
  const latestActivities: Activity[] = getLatestActivities(workerData.activities);

  const mainFields = [
    { key: 'securityId', label: 'Security ID', icon: <Security /> },
    { key: 'fullName', label: 'Nome Completo', icon: <Person /> },
    { key: 'supplier', label: 'Fornecedor', icon: <Business /> },
    { key: 'role', label: 'Cargo', icon: <CardTravel /> },
    { key: 'birthDate', label: 'Data de Nascimento', icon: <CalendarToday /> },
  ];
  
  const allKeys = Object.keys(workerData).filter(k => k !== 'photoUrl' && k !== 'activities');
  const extraFields = allKeys.filter(k => !['securityId','firstName','lastName','supplier','role','birthDate'].includes(k));

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
      py: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant={isSmallMobile ? 'h5' : 'h4'}
            component="h1"
            sx={{ 
              fontWeight: 700,
              color: 'text.primary',
              mb: 1
            }}
          >
            Cartão de identificação
          </Typography>
          <Box 
            sx={{ 
              width: 60, 
              height: 4, 
              bgcolor: 'primary.main', 
              mx: 'auto', 
              borderRadius: 2 
            }} 
          />
        </Box>

        <Card 
          elevation={8}
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            background: 'white'
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  >
                    <Avatar
                      src={workerData.photoUrl}
                      alt="Foto do trabalhador"
                      sx={{ 
                        width: { xs: 120, sm: 140, md: 160 },
                        height: { xs: 120, sm: 140, md: 160 },
                        border: '4px solid',
                        borderColor: 'primary.light',
                        boxShadow: 4
                      }}
                    />
                  </Badge>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  {mainFields.map((field) => (
                    <Grid item xs={12} sm={6} key={field.key}>
                      <InfoField
                        icon={field.icon}
                        label={field.label}
                        value={
                          field.key === 'fullName'
                            ? `${workerData.firstName} ${workerData.lastName}`
                            : field.key === 'birthDate'
                              ? formatDate(new Date((workerData as any)[field.key]))
                              : (workerData as any)[field.key]
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
                {extraFields.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {!showMore ? (
                      <Button variant="outlined" size="small" onClick={() => setShowMore(true)}>
                        Ver mais informações
                      </Button>
                    ) : (
                      <Grid container spacing={2} sx={{ mt: 2 }}>
                        {extraFields.map((key) => (
                          <Grid item xs={12} sm={6} key={key}>
                            <InfoField
                              icon={<Person />}
                              label={key.charAt(0).toUpperCase() + key.slice(1)}
                              value={typeof (workerData as any)[key] === 'string' ? (workerData as any)[key] : JSON.stringify((workerData as any)[key])}
                            />
                          </Grid>
                        ))}
                        <Grid item xs={12}>
                          <Button variant="outlined" size="small" color="secondary" onClick={() => setShowMore(false)}>
                            Ocultar informações
                          </Button>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>

          <Divider />

          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h5" 
                component="h2"
                sx={{ 
                  fontWeight: 700,
                  mb: 1,
                  fontSize: { xs: '1.3rem', sm: '1.5rem' }
                }}
              >
                Atividades Recentes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Acompanhe o progresso das atividades do trabalhador
              </Typography>
            </Box>

            {latestActivities.length === 0 ? (
              <Paper 
                sx={{ 
                  p: 6, 
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  border: '2px dashed',
                  borderColor: 'grey.300'
                }}
              >
                <Schedule sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Nenhuma atividade encontrada
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  As atividades aparecerão aqui quando forem atribuídas
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={3}>
                {getLatestActivities(workerData.activities).map((activity, idx) => (
                  <ActivityCard 
                    key={`${activity.code}-${idx}`} 
                    activity={activity} 
                  />
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

      </Container>
    </Box>
  );
}


